import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { tasksAPI, teamsAPI, reportsAPI } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DatePicker from '../components/DatePicker';
import { getTodayKualaLumpurISODate, toKualaLumpurISODate } from '../utils/dateFormat';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/Dashboard.module.css';

const fmtRM = (val) => `RM ${Number(val || 0).toLocaleString('en-MY', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const KUALA_LUMPUR_TIMEZONE = 'Asia/Kuala_Lumpur';
const toISODate = (date) => toKualaLumpurISODate(date);
const shortMonthLabel = (year, month) => {
  const d = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: KUALA_LUMPUR_TIMEZONE }).format(d);
};

const normalizeDate = (value) => {
  if (!value) return '';
  return String(value).split('T')[0];
};

const parseDateParts = (value) => {
  const match = normalizeDate(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
  };
};

const compareDateOnlyDesc = (a, b) => {
  const dateA = normalizeDate(a);
  const dateB = normalizeDate(b);
  return dateB.localeCompare(dateA);
};

const getYearRange = (start, end) => {
  const years = [];
  for (let y = start.getFullYear(); y <= end.getFullYear(); y += 1) {
    years.push(y);
  }
  return years;
};

const getDayRange = (start, end) => {
  const days = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  while (cursor <= endDay) {
    days.push(toISODate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
};

const getHourRange = () => Array.from({ length: 24 }, (_, index) => `${String(index).padStart(2, '0')}:00`);

const getTaskHourLabel = (task) => {
  const raw = task.start_time || task.assigned_at || task.created_at;
  if (!raw) return '00:00';

  if (typeof raw === 'string') {
    const hour = raw.split(':')[0];
    return `${String(hour).padStart(2, '0')}:00`;
  }

  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '00:00';
  const hour = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    hour12: false,
    timeZone: KUALA_LUMPUR_TIMEZONE,
  }).format(d);
  return `${hour}:00`;
};

const getTaskDayLabel = (task) => {
  const parts = parseDateParts(task.task_date);
  if (!parts) return '';
  return String(parts.day).padStart(2, '0');
};

const getTaskMonthLabel = (task) => {
  const parts = parseDateParts(task.task_date);
  if (!parts) return '';
  return shortMonthLabel(parts.year, parts.month);
};

const Dashboard = () => {
  const { user, can } = useAuth();
  const { t } = useLocalization();
  const canViewFinancials = can('financials', 'view');
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const [reportType, setReportType] = useState('monthly');
  const [startDate, setStartDate] = useState(toISODate(firstOfMonth));
  const [endDate, setEndDate] = useState(toISODate(now));
  const [stats, setStats] = useState({
    totalTasks: 0,
    teamsCount: 0,
    completedTasks: 0,
    inProgressTasks: 0,
    pendingTasks: 0,
  });
  const [trendData, setTrendData] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [recentTasks, setRecentTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [costSummary, setCostSummary] = useState({
    today: { cost: 0, revenue: 0, profit: 0 },
    month: { cost: 0, revenue: 0, profit: 0 },
    year: { cost: 0, revenue: 0, profit: 0 },
  });
  const [costLoading, setCostLoading] = useState(true);

  const periodLabel = useMemo(() => `${startDate} ${t('common_to', 'to')} ${endDate}`, [startDate, endDate, t]);

  const statusLabel = (status) => {
    const map = {
      pending: t('tasks_status_pending', 'Pending'),
      in_progress: t('tasks_status_in_progress', 'In Progress'),
      completed: t('tasks_status_completed', 'Completed'),
      cancelled: t('tasks_status_cancelled', 'Cancelled'),
    };
    return map[status] || status;
  };

  const handleReportTypeChange = (nextType) => {
    if (nextType === reportType) return;

    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    setReportType(nextType);

    if (nextType === 'daily') {
      const day = getTodayKualaLumpurISODate();
      setStartDate(day);
      setEndDate(day);
      return;
    }

    if (nextType === 'monthly') {
      const start = new Date(year, month, 1);
      const end = new Date(year, month + 1, 0);
      setStartDate(toISODate(start));
      setEndDate(toISODate(end));
      return;
    }

    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);
    setStartDate(toISODate(start));
    setEndDate(toISODate(end));
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const startParts = parseDateParts(startDate);
      const endParts = parseDateParts(endDate);
      if (!startParts || !endParts || normalizeDate(startDate) > normalizeDate(endDate)) {
        setErrorMessage(t('dashboard_invalid_date_range', 'Please select a valid date range'));
        setStats({
          totalTasks: 0,
          teamsCount: 0,
          completedTasks: 0,
          inProgressTasks: 0,
          pendingTasks: 0,
        });
        setTrendData([]);
        setTeamData([]);
        setRecentTasks([]);
        return;
      }

      const params = {
        startDate: normalizeDate(startDate),
        endDate: normalizeDate(endDate),
      };

      const [tasksResponse, teamsResponse] = await Promise.all([
        tasksAPI.getAll(params),
        teamsAPI.getAll(),
      ]);

      const selectedTasks = Array.isArray(tasksResponse.data) ? tasksResponse.data : [];
      const sortedTasks = [...selectedTasks].sort((a, b) => {
        const dateOrder = compareDateOnlyDesc(a.task_date, b.task_date);
        if (dateOrder !== 0) return dateOrder;
        return Number(b.id) - Number(a.id);
      });
      setRecentTasks(sortedTasks.slice(0, 6));

      const start = new Date(startParts.year, startParts.month - 1, startParts.day);
      const end = new Date(endParts.year, endParts.month - 1, endParts.day);

      let totalTasks = selectedTasks.length;
      let completedTasks = selectedTasks.filter((t) => t.status === 'completed').length;
      let inProgressTasks = selectedTasks.filter((t) => t.status === 'in_progress').length;
      let pendingTasks = selectedTasks.filter((t) => t.status === 'pending').length;
      let chartRows = [];
      let teamRows = [];

      if (reportType === 'daily') {
        const hourKeys = getHourRange();
        const grouped = new Map();
        const groupedTeams = new Map();

        hourKeys.forEach((hour) => {
          grouped.set(hour, { label: hour, total_tasks: 0, completed_tasks: 0 });
          groupedTeams.set(hour, { label: hour, tasks_assigned: 0, completed: 0 });
        });

        selectedTasks.forEach((task) => {
          const key = getTaskHourLabel(task);
          const row = grouped.get(key) || { label: key, total_tasks: 0, completed_tasks: 0 };
          row.total_tasks += 1;
          if (task.status === 'completed') row.completed_tasks += 1;
          grouped.set(key, row);

          if (task.team_id || task.team_name) {
            const teamRow = groupedTeams.get(key) || { label: key, tasks_assigned: 0, completed: 0 };
            teamRow.tasks_assigned += 1;
            if (task.status === 'completed') teamRow.completed += 1;
            groupedTeams.set(key, teamRow);
          }
        });

        chartRows = hourKeys.map((hour) => grouped.get(hour));
        teamRows = hourKeys.map((hour) => groupedTeams.get(hour));
      } else if (reportType === 'monthly') {
        const dayKeys = getDayRange(start, end).map((day) => {
          const parts = parseDateParts(day);
          return parts ? String(parts.day).padStart(2, '0') : '';
        });
        const grouped = new Map();
        const groupedTeams = new Map();

        dayKeys.forEach((day) => {
          grouped.set(day, { label: day, total_tasks: 0, completed_tasks: 0 });
          groupedTeams.set(day, { label: day, tasks_assigned: 0, completed: 0 });
        });

        selectedTasks.forEach((task) => {
          const key = getTaskDayLabel(task);
          const row = grouped.get(key) || { label: key, total_tasks: 0, completed_tasks: 0 };
          row.total_tasks += 1;
          if (task.status === 'completed') row.completed_tasks += 1;
          grouped.set(key, row);

          if (task.team_id || task.team_name) {
            const teamRow = groupedTeams.get(key) || { label: key, tasks_assigned: 0, completed: 0 };
            teamRow.tasks_assigned += 1;
            if (task.status === 'completed') teamRow.completed += 1;
            groupedTeams.set(key, teamRow);
          }
        });

        chartRows = dayKeys.map((day) => grouped.get(day));
        teamRows = dayKeys.map((day) => groupedTeams.get(day));
      } else {
        const years = getYearRange(start, end);
        const monthKeys = [];
        years.forEach((year) => {
          for (let month = 1; month <= 12; month += 1) {
            const key = `${year}-${String(month).padStart(2, '0')}`;
            const date = new Date(year, month - 1, 1);
            if (date >= new Date(start.getFullYear(), start.getMonth(), 1) && date <= new Date(end.getFullYear(), end.getMonth(), 1)) {
              monthKeys.push(key);
            }
          }
        });

        const grouped = new Map();
        const groupedTeams = new Map();

        monthKeys.forEach((key) => {
          const [year, month] = key.split('-').map(Number);
          const label = shortMonthLabel(year, month);
          grouped.set(key, { label, total_tasks: 0, completed_tasks: 0 });
          groupedTeams.set(key, { label, tasks_assigned: 0, completed: 0 });
        });

        selectedTasks.forEach((task) => {
          const normalized = normalizeDate(task.task_date);
          const parts = parseDateParts(normalized);
          if (!parts) return;
          const key = `${parts.year}-${String(parts.month).padStart(2, '0')}`;
          const row = grouped.get(key) || { label: getTaskMonthLabel(task), total_tasks: 0, completed_tasks: 0 };
          row.total_tasks += 1;
          if (task.status === 'completed') row.completed_tasks += 1;
          grouped.set(key, row);

          if (task.team_id || task.team_name) {
            const teamRow = groupedTeams.get(key) || { label: getTaskMonthLabel(task), tasks_assigned: 0, completed: 0 };
            teamRow.tasks_assigned += 1;
            if (task.status === 'completed') teamRow.completed += 1;
            groupedTeams.set(key, teamRow);
          }
        });

        chartRows = monthKeys.map((key) => grouped.get(key));
        teamRows = monthKeys.map((key) => groupedTeams.get(key));
      }

      setStats({
        totalTasks,
        teamsCount: Array.isArray(teamsResponse.data) ? teamsResponse.data.length : 0,
        completedTasks,
        inProgressTasks,
        pendingTasks,
      });
      setTrendData(chartRows);
      setTeamData(teamRows);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setErrorMessage(error.response?.data?.message || t('dashboard_load_failed', 'Failed to load dashboard analytics'));
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, reportType, t]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const fetchCostSummary = async () => {
      if (!canViewFinancials) {
        setCostSummary({
          today: { cost: 0, revenue: 0, profit: 0 },
          month: { cost: 0, revenue: 0, profit: 0 },
          year: { cost: 0, revenue: 0, profit: 0 },
        });
        setCostLoading(false);
        return;
      }
      try {
        setCostLoading(true);
        const res = await reportsAPI.costSummary();
        setCostSummary(res.data || {
          today: { cost: 0, revenue: 0, profit: 0 },
          month: { cost: 0, revenue: 0, profit: 0 },
          year: { cost: 0, revenue: 0, profit: 0 },
        });
      } catch (e) {
        setCostSummary({
          today: { cost: 0, revenue: 0, profit: 0 },
          month: { cost: 0, revenue: 0, profit: 0 },
          year: { cost: 0, revenue: 0, profit: 0 },
        });
      } finally {
        setCostLoading(false);
      }
    };
    fetchCostSummary();
  }, [canViewFinancials]);

  const completionRate = stats.totalTasks > 0
    ? Math.round((stats.completedTasks / stats.totalTasks) * 100)
    : 0;

  return (
    <div className={styles.container}>
      <div className={styles.hero}>
        <div>
          <h1>{t('dashboard_admin_title', 'Admin Dashboard')}</h1>
          <p className={styles.welcome}>{t('dashboard_welcome_back', 'Welcome back')}, {user?.first_name || user?.username}</p>
          <p className={styles.period}>{periodLabel}</p>
        </div>

        <div className={styles.controls}>
          <div className={styles.radioGroup}>
            <button
              type="button"
              className={`${styles.segmentBtn} ${reportType === 'daily' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleReportTypeChange('daily')}
            >
              {t('common_daily', 'Daily')}
            </button>
            <button
              type="button"
              className={`${styles.segmentBtn} ${reportType === 'monthly' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleReportTypeChange('monthly')}
            >
              {t('common_monthly', 'Monthly')}
            </button>
            <button
              type="button"
              className={`${styles.segmentBtn} ${reportType === 'yearly' ? styles.segmentBtnActive : ''}`}
              onClick={() => handleReportTypeChange('yearly')}
            >
              {t('common_yearly', 'Yearly')}
            </button>
          </div>

          <div className={styles.dateRange}>
            <DatePicker
              value={startDate}
              onChange={(val) => setStartDate(val)}
              placeholder={t('tasks_start_date', 'Start date')}
              className={styles.dateInput}
            />
            <DatePicker
              value={endDate}
              onChange={(val) => setEndDate(val)}
              placeholder={t('tasks_end_date', 'End date')}
              className={styles.dateInput}
            />
          </div>
        </div>
      </div>

      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.icon}><i className="bi bi-list-task" aria-hidden="true"></i></div>
          <div className={styles.content}>
            <h3>{t('dashboard_total_tasks', 'Total Tasks')}</h3>
            <p className={styles.value}>{stats.totalTasks}</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.icon}><i className="bi bi-people-fill" aria-hidden="true"></i></div>
          <div className={styles.content}>
            <h3>{t('dashboard_active_teams', 'Active Teams')}</h3>
            <p className={styles.value}>{stats.teamsCount}</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.icon}><i className="bi bi-check2-circle" aria-hidden="true"></i></div>
          <div className={styles.content}>
            <h3>{t('dashboard_completed_tasks', 'Completed Tasks')}</h3>
            <p className={styles.value}>{stats.completedTasks}</p>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.icon}><i className="bi bi-graph-up-arrow" aria-hidden="true"></i></div>
          <div className={styles.content}>
            <h3>{t('dashboard_completion_rate', 'Completion Rate')}</h3>
            <p className={styles.value}>{completionRate}%</p>
          </div>
        </div>
      </div>

      {canViewFinancials && (
        <div className={styles.section} style={{ marginTop: '1rem' }}>
          <h2>{t('dashboard_financial_summary', 'Financial Summary')}</h2>
          <div className={styles.statsGrid}>
            <div className={`${styles.statCard} ${styles.costCard}`}>
              <div className={styles.icon}><i className="bi bi-calendar-day" aria-hidden="true"></i></div>
              <div className={styles.content}>
                <h3>{t('dashboard_today', 'Today')}</h3>
                <p className={`${styles.subValue} ${styles.profitLine} ${Number(costSummary.today?.profit || 0) >= 0 ? styles.profitTextPositive : styles.profitTextNegative}`}>
                  {costLoading ? '…' : `${t('kanban_financial_profit', 'Profit')} ${fmtRM(costSummary.today?.profit)}`}
                </p>
                <p className={`${styles.subValue} ${styles.revenueText}`}>{costLoading ? '…' : `${t('kanban_financial_revenue', 'Revenue')} ${fmtRM(costSummary.today?.revenue)}`}</p>
                <p className={`${styles.subValue} ${styles.costText}`}>{costLoading ? '…' : `${t('reports_total_cost', 'Total Cost')} ${fmtRM(costSummary.today?.cost)}`}</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.costCard}`}>
              <div className={styles.icon}><i className="bi bi-calendar3" aria-hidden="true"></i></div>
              <div className={styles.content}>
                <h3>{t('dashboard_this_month', 'This Month')}</h3>
                <p className={`${styles.subValue} ${styles.profitLine} ${Number(costSummary.month?.profit || 0) >= 0 ? styles.profitTextPositive : styles.profitTextNegative}`}>
                  {costLoading ? '…' : `${t('kanban_financial_profit', 'Profit')} ${fmtRM(costSummary.month?.profit)}`}
                </p>
                <p className={`${styles.subValue} ${styles.revenueText}`}>{costLoading ? '…' : `${t('kanban_financial_revenue', 'Revenue')} ${fmtRM(costSummary.month?.revenue)}`}</p>
                <p className={`${styles.subValue} ${styles.costText}`}>{costLoading ? '…' : `${t('reports_total_cost', 'Total Cost')} ${fmtRM(costSummary.month?.cost)}`}</p>
              </div>
            </div>
            <div className={`${styles.statCard} ${styles.costCard}`}>
              <div className={styles.icon}><i className="bi bi-calendar2" aria-hidden="true"></i></div>
              <div className={styles.content}>
                <h3>{t('dashboard_this_year', 'This Year')}</h3>
                <p className={`${styles.subValue} ${styles.profitLine} ${Number(costSummary.year?.profit || 0) >= 0 ? styles.profitTextPositive : styles.profitTextNegative}`}>
                  {costLoading ? '…' : `${t('kanban_financial_profit', 'Profit')} ${fmtRM(costSummary.year?.profit)}`}
                </p>
                <p className={`${styles.subValue} ${styles.revenueText}`}>{costLoading ? '…' : `${t('kanban_financial_revenue', 'Revenue')} ${fmtRM(costSummary.year?.revenue)}`}</p>
                <p className={`${styles.subValue} ${styles.costText}`}>{costLoading ? '…' : `${t('reports_total_cost', 'Total Cost')} ${fmtRM(costSummary.year?.cost)}`}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {errorMessage && <p className={styles.error}>{errorMessage}</p>}

      <div className={styles.analyticsGrid}>
        <div className={styles.section}>
          <h2>{t('dashboard_task_trend', 'Task Trend')}</h2>
          {loading ? (
            <p>{t('dashboard_loading_analytics', 'Loading analytics...')}</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={trendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d8e0ef" />
                <XAxis dataKey="label" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="total_tasks" stroke="#2563eb" strokeWidth={2.5} name={t('dashboard_total_tasks', 'Total Tasks')} />
                <Line type="monotone" dataKey="completed_tasks" stroke="#16a34a" strokeWidth={2.5} name={t('tasks_status_completed', 'Completed')} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className={styles.section}>
          <h2>{t('dashboard_team_completion', 'Team Completion')}</h2>
          {loading ? (
            <p>{t('teams_loading', 'Loading teams...')}</p>
          ) : teamData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={teamData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d8e0ef" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="tasks_assigned" stroke="#0ea5e9" strokeWidth={2.5} name={t('dashboard_assigned', 'Assigned')} />
                <Line type="monotone" dataKey="completed" stroke="#f97316" strokeWidth={2.5} name={t('tasks_status_completed', 'Completed')} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className={styles.empty}>{t('dashboard_no_team_data', 'No team data for selected period.')}</p>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <h2>{t('dashboard_latest_tasks_period', 'Latest Tasks (Selected Period)')}</h2>
        {loading ? (
          <p>{t('tasks_loading', 'Loading tasks...')}</p>
        ) : recentTasks.length > 0 ? (
          <div className={styles.tasksList}>
            {recentTasks.map((task) => (
              <div key={task.id} className={styles.taskItem}>
                <div className={styles.taskInfo}>
                  <h4>{task.title}</h4>
                  <p>{task.description}</p>
                </div>
                <span className={`${styles.status} ${styles[task.status]}`}>
                  {statusLabel(task.status)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.empty}>{t('dashboard_no_tasks_period', 'No tasks for selected period')}</p>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
